import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dlpService, DlpExceptionRequest } from '../services/dlp.service';
import ReactMarkdown from 'react-markdown';
import seahLogo from '../assets/seah-logo.svg';
import './DlpReportPage.css';

interface DlpReportPageProps {}

const DlpReportPage: React.FC<DlpReportPageProps> = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>('summary');
  const [isLoading, setIsLoading] = useState(false);
  const [isPublished, setIsPublished] = useState<boolean>(false);
  const [summaryContent, setSummaryContent] = useState<string>('');
  const [attachments, setAttachments] = useState<any[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false); // 데이터 로드 완료 여부 추적
  
  // PDF 모드 확인 (URL 쿼리 파라미터)
  const [isPdfMode, setIsPdfMode] = useState(false);

  useEffect(() => {
    // URL 쿼리 파라미터 확인
    const urlParams = new URLSearchParams(window.location.search);
    const pdfParam = urlParams.get('pdf');
    const periodParam = urlParams.get('period');
    const companyParam = urlParams.get('company');
    
    if (pdfParam === 'true') {
      setIsPdfMode(true);
      if (periodParam) setSelectedPeriod(periodParam);
      if (companyParam) setSelectedCompany(decodeURIComponent(companyParam));
    }
    
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPeriod && selectedCompany) {
      setDataLoaded(false); // 새로운 period/company 선택 시 데이터 로드 상태 초기화
      loadReportData(selectedPeriod, selectedCompany);
      checkPublishStatus(selectedPeriod, selectedCompany);
    } else {
      setDataLoaded(false); // period나 company가 없으면 데이터 로드 상태 초기화
    }
  }, [selectedPeriod, selectedCompany]);

  // PDF 모드에서 Puppeteer가 데이터 로딩 상태를 확인할 수 있도록 window 객체에 노출
  useEffect(() => {
    if (isPdfMode) {
      (window as any).__pdfDataLoaded = dataLoaded;
      (window as any).__pdfIsLoading = isLoading;
      (window as any).__pdfSummaryContent = summaryContent;
      (window as any).__pdfAttachments = attachments;
    }
  }, [isPdfMode, dataLoaded, isLoading, summaryContent, attachments]);

  const loadInitialData = async () => {
    // URL 파라미터 확인 (PDF 모드 체크)
    const urlParams = new URLSearchParams(window.location.search);
    const pdfParam = urlParams.get('pdf');
    const companyParam = urlParams.get('company');
    const periodParam = urlParams.get('period');
    const isPdfModeFromUrl = pdfParam === 'true';
    
    // PDF 모드일 때는 URL 파라미터에서 직접 데이터 로드
    if (isPdfModeFromUrl && companyParam && periodParam) {
      // PDF 모드일 때 window 객체 초기화
      (window as any).__pdfDataLoaded = false;
      (window as any).__pdfIsLoading = true;
      (window as any).__pdfSummaryContent = '';
      (window as any).__pdfAttachments = [];
      
      try {
        // 회사 목록 로드
        const companies = await dlpService.getPermittedCompanies();
        setAvailableCompanies(companies);
        setSelectedCompany(decodeURIComponent(companyParam));
        setSelectedPeriod(periodParam);
        
        // URL 파라미터에서 직접 데이터 로드 (loadReportData가 로딩 상태를 관리함)
        await loadReportData(periodParam, decodeURIComponent(companyParam));
        await checkPublishStatus(periodParam, decodeURIComponent(companyParam));
      } catch (error) {
        console.error('Failed to load PDF data:', error);
        setIsLoading(false); // 에러 발생 시에만 로딩 상태 해제
        (window as any).__pdfDataLoaded = true;
        (window as any).__pdfIsLoading = false;
      }
      return;
    }
    
    setIsLoading(true);
    try {
      // 인가된 회사 목록 가져오기 (먼저)
      const companies = await dlpService.getPermittedCompanies();
      console.log('Loaded companies:', companies); // 디버깅용
      setAvailableCompanies(companies);
      
      // 관리자/에디터/리뷰어는 회사 선택 후 기간 선택
      // viewer는 자동 선택
      if (user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') {
        // 관리자/에디터/리뷰어는 회사 선택을 기다림
        if (companies.length === 1) {
          setSelectedCompany(companies[0]);
        }
      } else {
        // viewer는 자동 선택
        if (companies.length > 0) {
          setSelectedCompany(companies[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load initial data:', error);
      alert('데이터를 불러오는데 실패했습니다: ' + (error as any)?.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 회사 선택 시 기간 목록 로드
  useEffect(() => {
    // URL 파라미터 확인 (PDF 모드 체크)
    const urlParams = new URLSearchParams(window.location.search);
    const pdfParam = urlParams.get('pdf');
    const isPdfModeFromUrl = pdfParam === 'true';
    
    // PDF 모드일 때는 period 목록만 로드 (자동 선택하지 않음)
    if (isPdfModeFromUrl && selectedCompany) {
      dlpService.getAvailablePeriods().then(periods => {
        setAvailablePeriods(periods);
      }).catch(error => {
        console.error('Failed to load periods:', error);
      });
      return;
    }
    
    if (selectedCompany) {
      loadPeriods();
    } else {
      setAvailablePeriods([]);
      setSelectedPeriod('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const loadPeriods = async () => {
    if (!selectedCompany) return;
    
    try {
      // URL 파라미터 확인 (PDF 모드 체크)
      const urlParams = new URLSearchParams(window.location.search);
      const pdfParam = urlParams.get('pdf');
      const periodParam = urlParams.get('period');
      const isPdfModeFromUrl = pdfParam === 'true';
      
      // 기간 목록 가져오기
      const periods = await dlpService.getAvailablePeriods();
      setAvailablePeriods(periods);

      // PDF 모드일 때는 URL에서 읽은 period를 유지 (자동 설정하지 않음)
      if (isPdfModeFromUrl && periodParam) {
        return;
      }

      // 가장 최근 period 가져오기
      const latestPeriod = await dlpService.getLatestPeriod();
      if (latestPeriod) {
        setSelectedPeriod(latestPeriod);
      } else if (periods.length > 0) {
        setSelectedPeriod(periods[0]);
      }
    } catch (error) {
      console.error('Failed to load periods:', error);
    }
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
  };

  const handleCompanyChange = (company: string) => {
    setSelectedCompany(company);
  };

  const loadReportData = async (period: string, company: string) => {
    if (!period || !company) return;
    
    setIsLoading(true);
    setDataLoaded(false); // 데이터 로드 시작
    
    // PDF 모드에서 window 객체 초기화
    if (isPdfMode) {
      (window as any).__pdfDataLoaded = false;
      (window as any).__pdfIsLoading = true;
    }
    
    try {
      // 요약 내용 로드
      const summary = await dlpService.getSummary(period, company);
      setSummaryContent(summary?.summaryContent || '');

      // 링크 목록 로드
      const links = await dlpService.getAttachments(period, company);
      setAttachments(links);
      
      setDataLoaded(true); // 데이터 로드 완료
      
      // PDF 모드에서 window 객체 즉시 업데이트 (React 상태 업데이트 전에)
      if (isPdfMode) {
        (window as any).__pdfDataLoaded = true;
        (window as any).__pdfIsLoading = false;
        (window as any).__pdfSummaryContent = summary?.summaryContent || '';
        (window as any).__pdfAttachments = links;
        console.log('[PDF Mode] Data loaded and window object updated');
      }
    } catch (error) {
      console.error('Failed to load report data:', error);
      setDataLoaded(true); // 에러 발생 시에도 완료로 표시 (에러 상태 표시를 위해)
      
      // PDF 모드에서 window 객체 업데이트
      if (isPdfMode) {
        (window as any).__pdfDataLoaded = true;
        (window as any).__pdfIsLoading = false;
      }
    } finally {
      setIsLoading(false);
      
      // PDF 모드에서 window 객체 최종 업데이트
      if (isPdfMode) {
        (window as any).__pdfIsLoading = false;
      }
    }
  };

  const checkPublishStatus = async (period: string, company: string) => {
    if (!period || !company) return;
    
    try {
      const published = await dlpService.getPublishStatus(period, company);
      setIsPublished(published);
    } catch (error) {
      console.error('Failed to check publish status:', error);
      setIsPublished(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedPeriod || !selectedCompany) {
      alert('기간과 회사를 선택해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      const blob = await dlpService.exportPdf(selectedPeriod, selectedCompany);
      
      // 파일명 생성
      const year = selectedPeriod.slice(0, 4);
      const month = selectedPeriod.slice(4);
      const fileName = `[${selectedCompany}] ${year}년 ${month}월 DLP 솔루션 운영보고서.pdf`;
      
      // 파일 다운로드
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('PDF download failed:', error);
      alert('PDF 다운로드에 실패했습니다: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'summary', label: '요약' },
    { id: 'policy', label: '정책현황' },
    { id: 'media', label: '매체제어' },
    { id: 'file-attachment', label: '파일첨부' },
    { id: 'output', label: '출력' },
    { id: 'software-block', label: '소프트웨어차단' },
    { id: 'website-block', label: '웹사이트차단' },
    { id: 'website-access', label: '당월 유해사이트 등록 현황' },
    { id: 'license', label: 'DLP 라이선스현황' },
  ];

  return (
    <div className={`dlp-report-page ${isPdfMode ? 'pdf-mode' : ''}`}>
      {!isPdfMode && (
        <div className="page-header">
          <h1>DLP 운영보고서</h1>
          <div className="header-controls">
            {/* 관리자/에디터/리뷰어는 회사 선택 먼저 */}
            {(user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') && (
              <div className="filter-group">
                <label htmlFor="company-select">회사:</label>
                <select
                  id="company-select"
                  value={selectedCompany}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">선택하세요</option>
                  {availableCompanies.map((company) => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))}
                </select>
                {availableCompanies.length === 0 && !isLoading && (
                  <span style={{ color: 'red', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                    회사 목록이 없습니다
                  </span>
                )}
              </div>
            )}
            <div className="filter-group">
              <label htmlFor="period-select">기간:</label>
              <select
                id="period-select"
                value={selectedPeriod}
                onChange={(e) => handlePeriodChange(e.target.value)}
                disabled={!selectedCompany || isLoading}
              >
                <option value="">{selectedCompany ? '선택하세요' : '회사를 먼저 선택하세요'}</option>
                {availablePeriods.map((period) => (
                  <option key={period} value={period}>
                    {period.slice(0, 4)}년 {period.slice(4)}월
                  </option>
                ))}
              </select>
              {selectedCompany && availablePeriods.length === 0 && !isLoading && (
                <span style={{ color: 'red', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                  기간 데이터가 없습니다
                </span>
              )}
            </div>
            <button 
              className="download-button" 
              onClick={handleDownloadPdf}
              disabled={!selectedPeriod || !selectedCompany}
            >
              PDF 다운로드
            </button>
          </div>
        </div>
      )}

      {!isPdfMode && !isPublished && selectedPeriod && selectedCompany && (
        <div className="publish-warning">
          <p>⚠️ 이 보고서는 아직 발행되지 않았습니다. 관리자에게 문의하세요.</p>
        </div>
      )}

      {isLoading ? (
        <div className="loading">로딩 중...</div>
      ) : !isPdfMode && !selectedPeriod ? (
        <div className="empty-state">
          <p>기간 데이터를 불러오는 중...</p>
        </div>
      ) : !isPdfMode && !selectedCompany ? (
        <div className="empty-state">
          <p>회사 데이터를 불러오는 중...</p>
        </div>
      ) : (
        <>
          {!isPdfMode && (
            <div className="tab-container">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div className="tab-content">
            {isPdfMode ? (
              // PDF 모드: 모든 탭을 한 번에 표시
              <>
                {/* 1페이지: 표지 */}
                <div className="pdf-page-break pdf-cover-page">
                  <div className="cover-content">
                    <div className="cover-logo">
                      <img src={seahLogo} alt="Seah Logo" className="cover-logo-image" />
                    </div>
                    <h1 className="cover-title">DLP 솔루션 운영보고서</h1>
                    <h2 className="cover-company">[{selectedCompany}]</h2>
                    <p className="cover-period">
                      {selectedPeriod ? `${selectedPeriod.slice(0, 4)}년 ${selectedPeriod.slice(4)}월` : ''}
                    </p>
                  </div>
                </div>
                {/* PDF 모드에서는 항상 SummaryTab 렌더링 (content가 없어도) */}
                <div className="pdf-page-break summary-page">
                  {isPdfMode && (isLoading || (selectedPeriod && selectedCompany && !dataLoaded)) ? (
                    <div className="tab-panel">
                      <h2>📄 요약</h2>
                      <div className="loading">기간 데이터를 불러오는 중...</div>
                    </div>
                  ) : (
                    <SummaryTab content={summaryContent} attachments={attachments} period={selectedPeriod} company={selectedCompany} />
                  )}
                </div>
                <div className="pdf-page-break">
                  <PolicyTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                </div>
                <div className="pdf-page-break">
                  <MediaControlTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                </div>
                <div className="pdf-page-break">
                  <FileAttachmentTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                </div>
                <div className="pdf-page-break">
                  <OutputTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                </div>
                <div className="pdf-page-break">
                  <SoftwareBlockTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                </div>
                <div className="pdf-page-break">
                  <WebsiteBlockTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                </div>
                <div className="pdf-page-break">
                  <WebsiteAccessTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                </div>
                <div className="pdf-page-break">
                  <LicenseTab period={selectedPeriod} company={selectedCompany} />
                </div>
              </>
            ) : (
              // 일반 모드: 활성 탭만 표시
              <>
                {activeTab === 'summary' && (
                  <SummaryTab content={summaryContent} attachments={attachments} period={selectedPeriod} company={selectedCompany} onTabChange={setActiveTab} />
                )}
                {activeTab === 'policy' && (
                  <PolicyTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                )}
                {activeTab === 'media' && (
                  <MediaControlTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                )}
                {activeTab === 'file-attachment' && (
                  <FileAttachmentTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                )}
                {activeTab === 'output' && (
                  <OutputTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                )}
                {activeTab === 'software-block' && (
                  <SoftwareBlockTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                )}
                {activeTab === 'website-block' && (
                  <WebsiteBlockTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                )}
                {activeTab === 'website-access' && (
                  <WebsiteAccessTab period={selectedPeriod} company={selectedCompany} attachments={attachments} />
                )}
                {activeTab === 'license' && (
                  <LicenseTab period={selectedPeriod} company={selectedCompany} />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// 요약 탭
const SummaryTab: React.FC<{ content: string; attachments: any[]; period?: string; company?: string; onTabChange?: (tabId: string) => void }> = ({ content, attachments, period, company, onTabChange }) => {
  // 유효한 탭 ID 목록
  const validTabIds = ['summary', 'policy', 'media', 'file-attachment', 'output', 'software-block', 'website-block', 'website-access', 'license'];
  
  // dlp_ops_data 링크 찾기
  const dlpOpsDataLink = attachments.find(link => link.linkId === 'dlp_ops_data');
  
  // 상세내용 섹션 생성
  const getDetailSection = () => {
    if (!dlpOpsDataLink || !dlpOpsDataLink.linkUrl || !period || !company) {
      return '';
    }
    
    // period 검증: 링크의 period가 현재 보고서 period와 일치하는지 확인
    if (dlpOpsDataLink.period && dlpOpsDataLink.period !== period) {
      return ''; // period가 일치하지 않으면 상세내용 섹션을 표시하지 않음
    }
    
    // period에서 YY년 MM월 형식으로 변환 (예: 202509 -> 25년 09월)
    const year = period.slice(2, 4);
    const month = period.slice(4, 6);
    
    // 마크다운 링크에서 대괄호를 이스케이프 처리 (\[ \] 사용)
    return `\n\n### 상세내용\n- [\\[${company}\\] ${year}년 ${month}월 DLP 운영데이터](${dlpOpsDataLink.linkUrl})`;
  };
  
  // content와 상세내용 섹션 결합
  const detailSection = getDetailSection();
  const hasContent = content && content.trim().length > 0;
  const hasDetailSection = detailSection && detailSection.trim().length > 0;
  
  // 요약 내용과 상세내용 결합 (dlp_ops_data 링크가 없으면 상세내용은 추가하지 않음)
  let fullContent = hasContent ? content : '';
  if (hasDetailSection) {
    fullContent = (fullContent ? fullContent + '\n' : '') + detailSection;
  }
  
  // PDF 모드일 때 (onTabChange가 없을 때) 앵커 링크(#로 시작) 제거
  if (!onTabChange && fullContent) {
    fullContent = fullContent
      // 완전한 마크다운 링크 형식 제거
      .replace(/\[([^\]]+)\]\(#([^)]+)(?:\s+"[^"]*")?\)/g, '$1')
      // 텍스트 뒤에 바로 오는 앵커 링크 제거 (예: "링크(#website-access)")
      .replace(/([^\s(]+)\(#([^)]+)\)/g, '$1')
      // 불완전한 링크 시작 제거 (예: "[링크" -> "링크")
      .replace(/\[([^\]]+)$/gm, '$1')
      // 혼자 있는 앵커 링크 제거 (예: "(#website-access)")
      .replace(/\(#([^)]+)\)/g, '');
  }
  
  // 최종적으로 표시할 내용이 있는지 확인
  const hasFullContent = fullContent && fullContent.trim().length > 0;
  
  return (
    <div className="tab-panel">
      <h2>📄 요약</h2>
      {hasFullContent ? (
        <div className="summary-content">
          <div className="markdown-content">
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => {
                  const href = props.href || '';
                  
                  // 앵커 링크인 경우 (#로 시작하고 유효한 탭 ID인 경우)
                  if (href.startsWith('#') && onTabChange) {
                    const tabId = href.substring(1); // # 제거
                    if (validTabIds.includes(tabId)) {
                      return (
                        <a
                          {...props}
                          href={href}
                          onClick={(e) => {
                            e.preventDefault();
                            onTabChange(tabId);
                          }}
                          style={{ cursor: 'pointer', color: '#3498db', textDecoration: 'underline' }}
                        >
                          {props.children || href}
                        </a>
                      );
                    }
                  }
                  
                  // 외부 링크인 경우 새 탭에서 열기
                  return (
                    <a 
                      {...props} 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {props.children || href}
                    </a>
                  );
                },
              }}
            >
              {fullContent.replace(/\{:target="_blank"\}/g, '')}
            </ReactMarkdown>
          </div>
        </div>
      ) : (
        <p className="no-content">요약 내용이 없습니다.</p>
      )}
    </div>
  );
};

// 정책현황 탭
const PolicyTab: React.FC<{ period: string; company: string; attachments: any[] }> = ({ period, company, attachments }) => {
  const [exceptionStats, setExceptionStats] = useState<Record<number, number>>({});
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setIsLoadingStats(true);
      try {
        const stats = await dlpService.getExceptionStats(period, company);
        setExceptionStats(stats);
      } catch (error) {
        console.error('Failed to load exception stats:', error);
      } finally {
        setIsLoadingStats(false);
      }
    };

    if (period && company) {
      loadData();
    }
  }, [period, company]);

  // 정책현황 표의 item 이름과 enum의 item_code 매핑 테이블
  // itemCode를 사용하여 정확한 매핑 보장
  // 정책별로 다른 item_code를 가진 항목(미분류, 메신저)은 제외하고 정책별 특수 매핑 사용
  const itemToItemCodeMapping: Record<string, number> = {
    // 매체별차단정책
    '매체예외등록': 101, // MEDIA_EXCEPTION_REG
    '모든매체예외등록': 102, // MEDIA_EXCEPTION_REG (같은 코드)
    '미사용으로 매체예외삭제': 103, // UNUSED_MEDIA_DELETE
    '메신저':105,
    // 공유폴더차단정책
    '공유폴더 정책 비활성화': 201, // SHARED_FOLDER_BLOCK_POLICY_DISABLE
    // 소프트웨어실행차단 정책
    'FTP유틸리티': 302, // FTP
    '메일클라이언트': 303, // MAIL_CLIENT
    '클라우드스토리지': 304, // CLOUD_STORAGE
    '메신저 (소프트웨어)': 301, // MESSENGER_SOFTWARE
    '원격제어': 305, // REMOTE_CONTROL
    '웹브라우저': 306, // WEB_BROWSER
    '미분류 (소프트웨어)': 307, // UNCLASSIFIED_SOFTWARE
    '모든카테고리허용': 308, // SOFTWARE_BLOCK_POLICY_DISABLE
    // 웹사이트접속 차단정책
    '웹메일': 401, // WEBMAIL
    '증권': 403, // SECURITIES
    '게임': 404, // GAME
    'SNS': 407, // SNS
    '보안위협': 409, // SECURITY_THREAT
    '성인/유해': 410, // ADULT_HARMFUL
    '뉴스': 406, // NEWS
    '쇼핑': 402, // SHOPPING
    '엔터테인먼트': 405, // ENTERTAINMENT
    '웹하드/클라우드 스토리지': 408, // WEBHARD_CLOUD_STORAGE
    '미분류 (웹사이트)': 411, // UNCLASSIFIED_WEBSITE
    '유해사이트차단(전사)': 412, // HARMFUL_SITE_BLOCK_COMPANYWIDE
    // 출력정책
    '모든문서': 501, // ALL_DOCUMENT_OUTPUT_BLOCK
    '개인정보문서': 502, // PERSONAL_INFO_DOCUMENT_OUTPUT_BLOCK
    // 승인정책
    '워터마크해제승인': 601, // WATERMARK_RELEASE
    '워터마크자동승인': 602, // WATERMARK_AUTO_APPROVAL
    // 기타
    '미사용라이선스': 701, // UNUSED_LICENSE
    '모든 정책 비활성화': 702 // ALL_POLICY_DISABLE
  };

  // 정책별 미분류/메신저 매핑 (정책 컨텍스트 기반)
  const getPolicySpecificItemCode = (itemName: string, policy: string): number | null => {
    if (itemName === '미분류') {
      if (policy === '소프트웨어실행차단 정책') {
        return 307; // UNCLASSIFIED_SOFTWARE
      } else if (policy === '웹사이트접속 차단정책') {
        return 411; // UNCLASSIFIED_WEBSITE
      }
    }
    if (itemName === '메신저') {
      if (policy === '소프트웨어실행차단 정책') {
        return 301; // MESSENGER_SOFTWARE
      } else if (policy === '파일첨부차단정책') {
        return 201; // MESSENGER_FILE_ATTACH
      }
    }
    return null;
  };

  // item 이름을 item_code로 매핑하여 count 가져오기
  const getExceptionCount = (itemName: string, policy?: string): number => {
    // 1. 정책별 특수 매핑 (미분류, 메신저 등) - 우선순위 높음
    if (policy) {
      const policySpecificItemCode = getPolicySpecificItemCode(itemName, policy);
      if (policySpecificItemCode !== null && exceptionStats[policySpecificItemCode]) {
        return exceptionStats[policySpecificItemCode];
      }
    }
    
    // 2. 매핑 테이블을 통한 item_code 조회
    const itemCode = itemToItemCodeMapping[itemName];
    if (itemCode !== undefined && exceptionStats[itemCode]) {
      return exceptionStats[itemCode];
    }
    
    return 0;
  };

  // 정책현황 데이터
  const policyData = [
    // 정보유출방지 - 매체별차단정책 (세아홀딩스면 25, 아니면 24)
    { function: '정보유출방지',  functionRowspan: company === '세아홀딩스' ? 25 : 24, policy: '매체별차단정책', policyRowspan: 3, media: '저장매체', mediaRowspan: 3, item: '매체예외등록', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '모든매체예외등록', standard: '-', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '미사용으로 매체예외삭제', standard: '-', exception: '-', colspan: null },
    // 정보유출방지 - 파일첨부차단정책
    { function: '', policy: '파일첨부차단정책', media: '카테고리', item: '메신저', standard: '차단', exception: '-', colspan: null },
    // 정보유출방지 - 공유폴더차단정책
    { function: '', policy: '공유폴더차단정책', media: '-', item: '공유폴더 정책 비활성화', standard: '차단', exception: '-', colspan: null },
    // 정보유출방지 - 소프트웨어실행차단 정책
    { function: '', policy: '소프트웨어실행차단 정책', policyRowspan: 8, media: '카테고리', mediaRowspan: 8, item: 'FTP유틸리티', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '메일클라이언트', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '클라우드스토리지', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '메신저 (소프트웨어)', standard: '일부차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '원격제어', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '웹브라우저', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '미분류 (소프트웨어)', standard: '-', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '모든카테고리허용', standard: '-', exception: '-', colspan: null },
    // 정보유출방지 - 웹사이트접속 차단정책 (세아홀딩스면 12, 아니면 11)
    { function: '', policy: '웹사이트접속 차단정책', policyRowspan: company === '세아홀딩스' ? 12 : 11, media: '카테고리', mediaRowspan: company === '세아홀딩스' ? 12 : 11, item: '웹메일', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '증권', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '게임', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: 'SNS', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '보안위협', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '성인/유해', standard: '차단', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '뉴스', standard: '허용', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '쇼핑', standard: '허용', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '엔터테인먼트', standard: '허용', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '웹하드/클라우드 스토리지', standard: '허용', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '미분류 (웹사이트)', standard: '-', exception: '-', colspan: null },
    // 유해사이트차단은 세아홀딩스일 때만 표시
    ...(company === '세아홀딩스' ? [{ function: '', policy: '', media: '', item: '유해사이트차단(전사)', standard: '-', exception: '-', colspan: null }] : []),
    // 출력물보안 - 출력정책 (출력물보안 4행 합치기, 출력정책 2행 합치기)
    { function: '출력물보안', functionRowspan: 4, policy: '출력정책', policyRowspan: 2, media: '차단기능', mediaRowspan: 2, item: '모든문서', standard: '워터마크적용', exception: '-', colspan: null },
    { function: '', policy: '', media: '', item: '개인정보문서', standard: '워터마크적용', exception: '-', colspan: null },
    // 출력물보안 - 승인정책 (승인정책 2행 합치기)
    { function: '', policy: '승인정책', policyRowspan: 2, media: '출력물승인설정', item: '워터마크해제승인', standard: '활성화', exception: '-', colspan: null },
    { function: '', policy: '', media: '워터마크자동승인', item: '워터마크자동승인', standard: '활성화', exception: '-', colspan: null },
    // 기타 - 3열 합치기
    { function: '기타', policy: '', media: '', item: '미사용라이선스', standard: '삭제', exception: '-', colspan: 3 },
  ];

  // link_id가 'dlp_attach_except'인 링크 찾기
  const exceptLink = attachments.find(link => link.linkId === 'dlp_attach_except');

  // 당월 예외 정책 신청 현황 데이터
  const [exceptionRequests, setExceptionRequests] = useState<DlpExceptionRequest[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);

  useEffect(() => {
    const loadExceptionRequests = async () => {
      if (!period || !company) {
        setExceptionRequests([]);
        return;
      }

      setIsLoadingRequests(true);
      try {
        const requests = await dlpService.getExceptionRequestsByPeriodAndCompany(period, company);
        setExceptionRequests(requests);
      } catch (error) {
        console.error('Failed to load exception requests:', error);
        setExceptionRequests([]);
      } finally {
        setIsLoadingRequests(false);
      }
    };

    loadExceptionRequests();
  }, [period, company]);

  // 예외 항목으로 정렬
  const sortedRequests = [...exceptionRequests].sort((a, b) => {
    const groupA = a.exceptGroup || '';
    const groupB = b.exceptGroup || '';
    if (groupA !== groupB) {
      return groupA.localeCompare(groupB, 'ko');
    }
    // 같은 예외 항목이면 예외 상세로 정렬
    const detailA = a.exceptDetailEnum?.itemName || a.exceptDetail || '';
    const detailB = b.exceptDetailEnum?.itemName || b.exceptDetail || '';
    return detailA.localeCompare(detailB, 'ko');
  });

  return (
    <div className="tab-panel policy-tab">
      <div className="policy-header-section">
        <h2>📋 정책현황</h2>
      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      
      {/* 기준 정책 및 예외 신청 현황 링크 */}
      <div style={{ marginBottom: '1.5rem' }}>
        <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
        <span style={{ marginRight: '0.5rem', fontSize: '1rem', fontWeight: 500 }}>기준 정책 및 예외 신청 현황</span>
        {exceptLink && (
          <a 
            href={exceptLink.linkUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#3498db',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            [{exceptLink.linkName}]
          </a>
        )}
      </div>
      </div>

      {/* 정책현황 표 */}
      <div className="policy-table-container">
        <table className="policy-table">
          <thead>
            <tr>
              <th>기능</th>
              <th>정책</th>
              <th>매체</th>
              <th>항목</th>
              <th>기준정책</th>
              <th>예외처리(당월) 현황</th>
            </tr>
          </thead>
          <tbody>
            {policyData.map((row, index) => {
              // rowspan이 적용 중인지 확인
              let skipFunction = false;
              let skipPolicy = false;
              let skipMedia = false;
              
              if (!row.functionRowspan && !row.function) {
                // 이전 행들에서 functionRowspan이 있는지 확인
                for (let i = index - 1; i >= 0; i--) {
                  const prevFunctionRowspan = policyData[i].functionRowspan;
                  if (prevFunctionRowspan) {
                    const remainingRows = prevFunctionRowspan - (index - i);
                    if (remainingRows > 0) {
                      skipFunction = true;
                      break;
                    }
                  }
                  if (policyData[i].function) break;
                }
              }
              
              if (!row.policyRowspan && !row.policy) {
                // 이전 행들에서 policyRowspan이 있는지 확인
                for (let i = index - 1; i >= 0; i--) {
                  const prevPolicyRowspan = policyData[i].policyRowspan;
                  if (prevPolicyRowspan) {
                    const remainingRows = prevPolicyRowspan - (index - i);
                    if (remainingRows > 0) {
                      skipPolicy = true;
                      break;
                    }
                  }
                  if (policyData[i].policy) break;
                }
              }
              
              if (!row.mediaRowspan && !row.media) {
                // 이전 행들에서 mediaRowspan이 있는지 확인
                for (let i = index - 1; i >= 0; i--) {
                  const prevMediaRowspan = policyData[i].mediaRowspan;
                  if (prevMediaRowspan) {
                    const remainingRows = prevMediaRowspan - (index - i);
                    if (remainingRows > 0) {
                      skipMedia = true;
                      break;
                    }
                  }
                  if (policyData[i].media) break;
                }
              }

              return (
                <tr key={index}>
                  {row.colspan ? (
                    <>
                      <td colSpan={row.colspan}>{row.function}</td>
                      <td>{row.item}</td>
                      <td>{row.standard}</td>
                      <td>
                        {isLoadingStats ? (
                          '-'
                        ) : (
                          (() => {
                            const count = getExceptionCount(row.item, row.policy);
                            return count > 0 ? count : '-';
                          })()
                        )}
                      </td>
                    </>
                  ) : (
                    <>
                      {!skipFunction && (
                        <td {...(row.functionRowspan ? { rowSpan: row.functionRowspan } : {})}>
                          {row.function}
                        </td>
                      )}
                      {!skipPolicy && (
                        <td {...(row.policyRowspan ? { rowSpan: row.policyRowspan } : {})}>
                          {row.policy}
                        </td>
                      )}
                      {!skipMedia && (
                        <td {...(row.mediaRowspan ? { rowSpan: row.mediaRowspan } : {})}>
                          {row.media}
                        </td>
                      )}
                      <td>{row.item}</td>
                      <td>{row.standard}</td>
                      <td>
                        {isLoadingStats ? (
                          '-'
                        ) : (
                          (() => {
                            const count = getExceptionCount(row.item, row.policy);
                            return count > 0 ? count : '-';
                          })()
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 당월 예외 정책 신청 현황 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>당월 예외 정책 신청 현황</span>
        </div>
        {isLoadingRequests ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>로딩 중...</div>
        ) : sortedRequests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            데이터가 없습니다.
          </div>
        ) : (
          <div className="policy-table-container">
            <table className="policy-table">
              <thead>
                <tr>
                  <th>예외 항목</th>
                  <th>예외 상세</th>
                  <th>신청 부서</th>
                  <th>신청자</th>
                  <th>실 정책 사용자</th>
                </tr>
              </thead>
              <tbody>
                {sortedRequests.map((request) => (
                  <tr key={request.id}>
                    <td>{request.exceptGroup || '-'}</td>
                    <td>{request.exceptDetailEnum?.itemName || request.exceptDetail || '-'}</td>
                    <td>{request.reqDepartment || '-'}</td>
                    <td>{request.reqName || '-'}</td>
                    <td>{request.permittedPc || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

// 매체제어 탭
const MediaControlTab: React.FC<{ period: string; company: string; attachments: any[] }> = ({ period, company, attachments }) => {
  // PDF 모드 확인
  const urlParams = new URLSearchParams(window.location.search);
  const isPdfMode = urlParams.get('pdf') === 'true';
  const { user } = useAuth();
  const [mediaAttachmentUserTop, setMediaAttachmentUserTop] = React.useState<any[]>([]);
  const [isLoadingMediaTop, setIsLoadingMediaTop] = React.useState(false);
  const [registeredMediaCount, setRegisteredMediaCount] = React.useState<number | null>(null);
  const [isLoadingRegCount, setIsLoadingRegCount] = React.useState(false);
  const [mediaAttachmentUsersOver1000, setMediaAttachmentUsersOver1000] = React.useState<any[]>([]);
  const [isLoadingOver1000, setIsLoadingOver1000] = React.useState(false);
  const [unusedUsb3Months, setUnusedUsb3Months] = React.useState<any[]>([]);
  const [isLoadingUnusedUsb, setIsLoadingUnusedUsb] = React.useState(false);

  const mediaLinks = attachments.filter((link) => 
    link.linkName?.includes('USB') || link.linkName?.includes('매체') || link.linkId?.includes('media')
  );

  React.useEffect(() => {
    if (period && company) {
      loadMediaAttachmentUserTop();
      loadRegisteredMediaCount();
      loadMediaAttachmentUsersOver1000();
      loadUnusedUsb3Months();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, company]);

  const loadMediaAttachmentUserTop = async () => {
    setIsLoadingMediaTop(true);
    try {
      const data = await dlpService.getMediaAttachmentUserTop(period, company);
      setMediaAttachmentUserTop(data);
    } catch (error) {
      console.error('Failed to load media attachment user top:', error);
      setMediaAttachmentUserTop([]);
    } finally {
      setIsLoadingMediaTop(false);
    }
  };

  const loadRegisteredMediaCount = async () => {
    setIsLoadingRegCount(true);
    try {
      const count = await dlpService.getRegisteredMediaCount(period, company);
      setRegisteredMediaCount(count);
    } catch (error) {
      console.error('Failed to load registered media count:', error);
      setRegisteredMediaCount(null);
    } finally {
      setIsLoadingRegCount(false);
    }
  };

  const loadMediaAttachmentUsersOver1000 = async () => {
    setIsLoadingOver1000(true);
    try {
      const data = await dlpService.getMediaAttachmentUsersOver1000(period, company);
      setMediaAttachmentUsersOver1000(data);
    } catch (error) {
      console.error('Failed to load media attachment users over 1000:', error);
      setMediaAttachmentUsersOver1000([]);
    } finally {
      setIsLoadingOver1000(false);
    }
  };

  const loadUnusedUsb3Months = async () => {
    setIsLoadingUnusedUsb(true);
    try {
      const data = await dlpService.getUnusedUsb3Months(period, company);
      setUnusedUsb3Months(data);
    } catch (error) {
      console.error('Failed to load unused USB 3 months:', error);
      setUnusedUsb3Months([]);
    } finally {
      setIsLoadingUnusedUsb(false);
    }
  };

  const handleToggleDisplay = async (id: number) => {
    try {
      await dlpService.toggleUnusedUsbDisplay(id);
      // 데이터 다시 로드
      loadUnusedUsb3Months();
    } catch (error) {
      console.error('Failed to toggle display:', error);
      alert('표시 상태 변경에 실패했습니다.');
    }
  };

  return (
    <div className="tab-panel">
      <h2>💾 매체제어</h2>
      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      
      {/* 보안 USB 등록 현황 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>보안 USB 등록 현황</span>
          {(() => {
            const usbLink = attachments.find(link => link.linkId === 'dlp_attach_usb');
            return usbLink ? (
              <a 
                href={usbLink.linkUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  marginLeft: '0.5rem',
                  color: '#3498db',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                [{usbLink.linkName}]
              </a>
            ) : null;
          })()}
        </div>
        {/* 1x1 표: 등록 매체 수량 */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>등록 매체 수량</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {isLoadingRegCount ? (
                    '로딩 중...'
                  ) : registeredMediaCount !== null ? (
                    registeredMediaCount.toLocaleString()
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 매체 첨부 사용자 TOP */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>매체 첨부 사용자 TOP3</span>
        </div>
        {/* 3x4 표: 부서, 이름, 사용빈도 */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>부서</th>
                <th>이름</th>
                <th>사용빈도</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingMediaTop ? (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : mediaAttachmentUserTop.length > 0 ? (
                mediaAttachmentUserTop.map((item, index) => (
                  <tr key={index}>
                    <td>{item.department || '-'}</td>
                    <td>{item.name || '-'}</td>
                    <td>{item.count ? item.count.toLocaleString() : '0'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 매체 첨부 1000건 이상 사용자 (세아베스틸만) */}
      {company === '세아베스틸' && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
            <span style={{ fontSize: '1rem', fontWeight: 500 }}>매체 첨부 1000건 이상 사용자</span>
          </div>
          {/* 3xN 표: 부서, 이름, 사용빈도 */}
          <div className="policy-table-container">
            <table className="policy-table">
              <thead>
                <tr>
                  <th>부서</th>
                  <th>이름</th>
                  <th>사용빈도</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingOver1000 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', padding: '1rem' }}>
                      로딩 중...
                    </td>
                  </tr>
                ) : mediaAttachmentUsersOver1000.length > 0 ? (
                  mediaAttachmentUsersOver1000.map((item, index) => (
                    <tr key={index}>
                      <td>{item.department || '-'}</td>
                      <td>{item.name || '-'}</td>
                      <td>{item.count ? item.count.toLocaleString() : '0'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', padding: '1rem' }}>
                      데이터가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 3개월 미사용 USB 내역 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>3개월 미사용 USB 내역</span>
        </div>
        {/* 4x2 표: 부서, 이름, USB명, 메모 (행은 데이터 수만큼 늘어남) */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>부서</th>
                <th>이름</th>
                <th>USB명</th>
                <th>메모</th>
                {!isPdfMode && (user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') && <th>표시</th>}
              </tr>
            </thead>
            <tbody>
              {isLoadingUnusedUsb ? (
                <tr>
                  <td colSpan={!isPdfMode && (user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') ? 5 : 4} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : unusedUsb3Months.length > 0 ? (
                unusedUsb3Months.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{item.department || '-'}</td>
                    <td>{item.name || '-'}</td>
                    <td>{item.usbName || '-'}</td>
                    <td>{item.memo || '-'}</td>
                    {!isPdfMode && (user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') && (
                      <td>
                        <button
                          onClick={() => item.id && handleToggleDisplay(item.id)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.875rem',
                            backgroundColor: item.displayYn === 'Y' ? '#4CAF50' : '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {item.displayYn === 'Y' ? '표시' : '숨김'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={!isPdfMode && (user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') ? 5 : 4} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666', fontStyle: 'italic', textAlign: 'right' }}>
          ※ PC에 매체를 연결한 후, 매체 내에서 파일을 실행하거나 파일을 생성·삭제하는 경우 로그에 기록되지 않으므로 수동 확인이 필요합니다.
        </div>
      </div>

    </div>
  );
};

// 파일첨부 탭
const FileAttachmentTab: React.FC<{ period: string; company: string; attachments: any[] }> = ({ period, company, attachments }) => {
  const [fileAttachmentSummary, setFileAttachmentSummary] = React.useState<{ attachmentCount: number; blockCount: number } | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = React.useState(false);
  const [fileAttachmentTop5, setFileAttachmentTop5] = React.useState<any[]>([]);
  const [fileBlockTop5, setFileBlockTop5] = React.useState<any[]>([]);
  const [webAttachmentUrlTop10, setWebAttachmentUrlTop10] = React.useState<any[]>([]);
  const [isLoadingTop5, setIsLoadingTop5] = React.useState(false);
  const [isLoadingBlockTop5, setIsLoadingBlockTop5] = React.useState(false);
  const [isLoadingUrlTop10, setIsLoadingUrlTop10] = React.useState(false);

  React.useEffect(() => {
    if (period && company) {
      loadFileAttachmentSummary();
      loadFileAttachmentTop5();
      loadFileBlockTop5();
      loadWebAttachmentUrlTop10();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, company]);

  const loadFileAttachmentSummary = async () => {
    setIsLoadingSummary(true);
    try {
      const summary = await dlpService.getFileAttachmentSummary(period, company);
      setFileAttachmentSummary(summary);
    } catch (error) {
      console.error('Failed to load file attachment summary:', error);
      setFileAttachmentSummary({ attachmentCount: 0, blockCount: 0 });
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const loadFileAttachmentTop5 = async () => {
    setIsLoadingTop5(true);
    try {
      const data = await dlpService.getFileAttachmentTop5(period, company);
      setFileAttachmentTop5(data);
    } catch (error) {
      console.error('Failed to load file attachment top 5:', error);
      setFileAttachmentTop5([]);
    } finally {
      setIsLoadingTop5(false);
    }
  };

  const loadFileBlockTop5 = async () => {
    setIsLoadingBlockTop5(true);
    try {
      const data = await dlpService.getFileBlockTop5(period, company);
      setFileBlockTop5(data);
    } catch (error) {
      console.error('Failed to load file block top 5:', error);
      setFileBlockTop5([]);
    } finally {
      setIsLoadingBlockTop5(false);
    }
  };

  const loadWebAttachmentUrlTop10 = async () => {
    setIsLoadingUrlTop10(true);
    try {
      const data = await dlpService.getWebAttachmentUrlTop10(period, company);
      setWebAttachmentUrlTop10(data);
    } catch (error) {
      console.error('Failed to load web attachment URL top 10:', error);
      setWebAttachmentUrlTop10([]);
    } finally {
      setIsLoadingUrlTop10(false);
    }
  };

  return (
    <div className="tab-panel">
      <h2>📎 파일첨부</h2>
      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      
      {/* 파일 첨부 건수 현황 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>파일 첨부 건수 현황</span>
          {(() => {
            const fileLink = attachments.find(link => link.linkId === 'dlp_attach_file');
            return fileLink ? (
              <a 
                href={fileLink.linkUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  marginLeft: '0.5rem',
                  color: '#3498db',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                [{fileLink.linkName}]
              </a>
            ) : null;
          })()}
        </div>
        {/* 2x2 표: 첨부 건수, 차단 건수 */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>첨부 건수	</th>
                <th>차단 건수	</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {isLoadingSummary ? (
                    '로딩 중...'
                  ) : fileAttachmentSummary ? (
                    fileAttachmentSummary.attachmentCount.toLocaleString()
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {isLoadingSummary ? (
                    '로딩 중...'
                  ) : fileAttachmentSummary ? (
                    fileAttachmentSummary.blockCount.toLocaleString()
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 파일 첨부 건수 프로그램 TOP 5 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>파일 첨부 건수 프로그램 TOP 5</span>
        </div>
        {/* 8x11 표: 프로그램명, 첨부 건 수, 구분, 1위 부서, 2위 부서, 3위 부서, 4위 부서, 5위 부서 */}
        {/* 각 프로그램마다 2행 (부서 행 + 건수 행) */}
        <div className="policy-table-container">
          <table className="policy-table file-attachment-top5-table">
            <thead>
              <tr>
                <th>프로그램명</th>
                <th>첨부 건 수</th>
                <th>구분</th>
                <th>1위 부서</th>
                <th>2위 부서</th>
                <th>3위 부서</th>
                <th>4위 부서</th>
                <th>5위 부서</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingTop5 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : fileAttachmentTop5.length > 0 ? (
                fileAttachmentTop5.map((item, index) => (
                  <tr key={index}>
                    {!item.isCountRow ? (
                      <>
                        <td rowSpan={2}>{item.programName || item.processName || '-'}</td>
                        <td rowSpan={2}>{item.totalCount ? item.totalCount.toLocaleString() : '-'}</td>
                        <td>{item.category}</td>
                        <td>{item.dept1 || '-'}</td>
                        <td>{item.dept2 || '-'}</td>
                        <td>{item.dept3 || '-'}</td>
                        <td>{item.dept4 || '-'}</td>
                        <td>{item.dept5 || '-'}</td>
                      </>
                    ) : (
                      <>
                        <td>{item.category}</td>
                        <td>{typeof item.dept1 === 'number' ? item.dept1.toLocaleString() : item.dept1}</td>
                        <td>{typeof item.dept2 === 'number' ? item.dept2.toLocaleString() : item.dept2}</td>
                        <td>{typeof item.dept3 === 'number' ? item.dept3.toLocaleString() : item.dept3}</td>
                        <td>{typeof item.dept4 === 'number' ? item.dept4.toLocaleString() : item.dept4}</td>
                        <td>{typeof item.dept5 === 'number' ? item.dept5.toLocaleString() : item.dept5}</td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 파일 차단 건수 프로그램 TOP 5 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>파일 차단 건수 프로그램 TOP 5</span>
        </div>
        {/* 8x11 표: 프로그램명, 첨부 건 수, 구분, 1위 부서, 2위 부서, 3위 부서, 4위 부서, 5위 부서 */}
        {/* 각 프로그램마다 2행 (부서 행 + 건수 행), 데이터가 있으면 최대 11행 */}
        <div className="policy-table-container">
          <table className="policy-table file-block-top5-table">
            <thead>
              <tr>
                <th>프로그램명</th>
                <th>첨부 건 수</th>
                <th>구분</th>
                <th>1위 부서</th>
                <th>2위 부서</th>
                <th>3위 부서</th>
                <th>4위 부서</th>
                <th>5위 부서</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingBlockTop5 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : fileBlockTop5.length > 0 ? (
                fileBlockTop5.map((item, index) => (
                  <tr key={index}>
                    {!item.isCountRow ? (
                      <>
                        <td rowSpan={2}>{item.programName || item.processName || '-'}</td>
                        <td rowSpan={2}>{item.totalCount ? item.totalCount.toLocaleString() : '-'}</td>
                        <td>{item.category}</td>
                        <td>{item.dept1 || '-'}</td>
                        <td>{item.dept2 || '-'}</td>
                        <td>{item.dept3 || '-'}</td>
                        <td>{item.dept4 || '-'}</td>
                        <td>{item.dept5 || '-'}</td>
                      </>
                    ) : (
                      <>
                        <td>{item.category}</td>
                        <td>{typeof item.dept1 === 'number' ? item.dept1.toLocaleString() : item.dept1}</td>
                        <td>{typeof item.dept2 === 'number' ? item.dept2.toLocaleString() : item.dept2}</td>
                        <td>{typeof item.dept3 === 'number' ? item.dept3.toLocaleString() : item.dept3}</td>
                        <td>{typeof item.dept4 === 'number' ? item.dept4.toLocaleString() : item.dept4}</td>
                        <td>{typeof item.dept5 === 'number' ? item.dept5.toLocaleString() : item.dept5}</td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 웹브라우저 첨부 URL TOP 10 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>웹브라우저 첨부 URL TOP 10</span>
        </div>
        {/* 2x11 표: 첨부 URL, 첨부건수 (데이터가 있으면 최대 11행) */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>첨부 URL</th>
                <th>첨부건수</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingUrlTop10 ? (
                <tr>
                  <td colSpan={2} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : webAttachmentUrlTop10.length > 0 ? (
                webAttachmentUrlTop10.map((item, index) => (
                  <tr key={index}>
                    <td>{item.url || '-'}</td>
                    <td>{item.count ? item.count.toLocaleString() : '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// 출력 탭
const OutputTab: React.FC<{ period: string; company: string; attachments: any[] }> = ({ period, company, attachments }) => {
  const [watermarkCount, setWatermarkCount] = useState<number>(0);
  const [isLoadingWatermark, setIsLoadingWatermark] = useState(false);
  const [deptUserOutputTop3, setDeptUserOutputTop3] = useState<any[]>([]);
  const [isLoadingDeptUser, setIsLoadingDeptUser] = useState(false);

  // 기간 포맷팅 함수 (YYYYMM -> YYYY년 MM월)
  const formatPeriod = (periodStr: string): string => {
    if (!periodStr || periodStr.length !== 6) return periodStr;
    const year = periodStr.substring(0, 4);
    const month = periodStr.substring(4, 6);
    return `${year}년 ${parseInt(month, 10)}월`;
  };

  // 전월 기간 계산
  const getPrevPeriod = (periodStr: string): string => {
    if (!periodStr || periodStr.length !== 6) return '';
    const year = parseInt(periodStr.substring(0, 4), 10);
    const month = parseInt(periodStr.substring(4, 6), 10);
    let prevYear = year;
    let prevMonth = month - 1;
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear = year - 1;
    }
    return `${prevYear}${String(prevMonth).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (period && company) {
      loadWatermarkCount();
      loadDeptUserOutputTop3();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, company]);

  const loadWatermarkCount = async () => {
    if (!period || !company) return;
    setIsLoadingWatermark(true);
    try {
      const count = await dlpService.getWatermarkRemovalCount(period, company);
      setWatermarkCount(count);
    } catch (error) {
      console.error('Failed to load watermark count:', error);
    } finally {
      setIsLoadingWatermark(false);
    }
  };

  const loadDeptUserOutputTop3 = async () => {
    if (!period || !company) return;
    setIsLoadingDeptUser(true);
    try {
      const data = await dlpService.getDeptUserOutputTop3(period, company);
      setDeptUserOutputTop3(data);
    } catch (error) {
      console.error('Failed to load dept user output top3:', error);
    } finally {
      setIsLoadingDeptUser(false);
    }
  };

  const prevPeriod = getPrevPeriod(period);

  return (
    <div className="tab-panel">
      <h2>🖨️ 출력</h2>
      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      
      {/* 워터마크 해제 신청 현황 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>워터마크 해제 신청 현황</span>
          {(() => {
            const watermarkLink = attachments.find(link => link.linkId === 'dlp_attach_watermark');
            return watermarkLink ? (
              <a 
                href={watermarkLink.linkUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  marginLeft: '0.5rem',
                  color: '#3498db',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                [{watermarkLink.linkName}]
              </a>
            ) : null;
          })()}
        </div>
        {/* 1x2 표: 해제 신청 건수 */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>해제 신청 건수</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {isLoadingWatermark ? (
                    '로딩 중...'
                  ) : (
                    watermarkCount.toLocaleString()
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 부서 및 사용자 출력 수 TOP 3 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>부서 및 사용자 출력 수 TOP 3</span>
        </div>
        {/* 6x11 표: 2중 헤더 (현재 기간/전월 비교), 행수는 데이터에 의해 가변적 */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th colSpan={3} style={{ textAlign: 'center' }}>{formatPeriod(period)}</th>
                <th colSpan={3} style={{ textAlign: 'center' }}>{prevPeriod ? formatPeriod(prevPeriod) : '전월'}</th>
              </tr>
              <tr>
                <th>부서</th>
                <th>이름</th>
                <th>출력수</th>
                <th>부서</th>
                <th>이름</th>
                <th>출력수</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingDeptUser ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : deptUserOutputTop3.length > 0 ? (
                deptUserOutputTop3.map((row, index) => (
                  <tr key={index}>
                    {row.isFirstInDept && (
                      <td rowSpan={row.deptRowspan}>{row.currentDept || '-'}</td>
                    )}
                    <td>{row.currentName || '-'}</td>
                    <td>{row.currentCount ? row.currentCount.toLocaleString() : '-'}</td>
                    {row.isFirstInDept && (
                      <td rowSpan={row.deptRowspan}>{row.prevDept || '-'}</td>
                    )}
                    <td>{row.prevName || '-'}</td>
                    <td>{row.prevCount ? row.prevCount.toLocaleString() : '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// 소프트웨어차단 탭
const SoftwareBlockTab: React.FC<{ period: string; company: string; attachments: any[] }> = ({ period, company, attachments }) => {
  const [softwareBlockCount, setSoftwareBlockCount] = useState<number>(0);
  const [isLoadingBlockCount, setIsLoadingBlockCount] = useState(false);
  const [softwareBlockUserTop5, setSoftwareBlockUserTop5] = useState<any[]>([]);
  const [isLoadingUserTop5, setIsLoadingUserTop5] = useState(false);
  const [categorySoftwareTop3, setCategorySoftwareTop3] = useState<any[]>([]);
  const [isLoadingCategoryTop3, setIsLoadingCategoryTop3] = useState(false);

  useEffect(() => {
    if (period && company) {
      loadSoftwareBlockCount();
      loadSoftwareBlockUserTop5();
      loadCategorySoftwareTop3();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, company]);

  const loadSoftwareBlockCount = async () => {
    if (!period || !company) return;
    setIsLoadingBlockCount(true);
    try {
      const count = await dlpService.getSoftwareBlockCount(period, company);
      setSoftwareBlockCount(count);
    } catch (error) {
      console.error('Failed to load software block count:', error);
    } finally {
      setIsLoadingBlockCount(false);
    }
  };

  const loadSoftwareBlockUserTop5 = async () => {
    if (!period || !company) return;
    setIsLoadingUserTop5(true);
    try {
      const data = await dlpService.getSoftwareBlockUserTop5(period, company);
      setSoftwareBlockUserTop5(data);
    } catch (error) {
      console.error('Failed to load software block user top5:', error);
    } finally {
      setIsLoadingUserTop5(false);
    }
  };

  const loadCategorySoftwareTop3 = async () => {
    if (!period || !company) return;
    setIsLoadingCategoryTop3(true);
    try {
      const data = await dlpService.getCategorySoftwareTop3(period, company);
      
      // 차단수 0 이상인 데이터만 필터링
      const filteredData = data.filter((row: any) => row.blockCount && row.blockCount > 0);
      
      // 카테고리별로 그룹화 (isFirstInCategory를 기준으로 그룹 분리)
      const categoryGroups: { [key: string]: any[] } = {};
      let currentCategory = '';
      let currentGroup: any[] = [];
      
      filteredData.forEach((row: any, index: number) => {
        // isFirstInCategory가 true인 경우 새로운 카테고리 시작
        if (row.isFirstInCategory) {
          // 이전 그룹이 있으면 저장
          if (currentGroup.length > 0 && currentCategory) {
            if (!categoryGroups[currentCategory]) {
              categoryGroups[currentCategory] = [];
            }
            categoryGroups[currentCategory].push(...currentGroup);
          }
          
          // 새 그룹 시작
          currentCategory = row.categoryName || '미분류';
          currentGroup = [row];
        } else {
          // 같은 카테고리의 행 추가
          currentGroup.push(row);
        }
      });
      
      // 마지막 그룹 추가
      if (currentGroup.length > 0 && currentCategory) {
        if (!categoryGroups[currentCategory]) {
          categoryGroups[currentCategory] = [];
        }
        categoryGroups[currentCategory].push(...currentGroup);
      }
      
      // 미분류를 제외한 카테고리와 미분류를 분리
      const categorizedGroups: { [key: string]: any[] } = {};
      let uncategorizedGroup: any[] = [];
      
      Object.keys(categoryGroups).forEach((categoryName) => {
        if (categoryName === '미분류' || categoryName === '' || !categoryName) {
          uncategorizedGroup = categoryGroups[categoryName];
        } else {
          categorizedGroups[categoryName] = categoryGroups[categoryName];
        }
      });
      
      // 카테고리별 그룹을 배열로 변환하고 rowspan 재계산
      const sortedRows: any[] = [];
      
      // 일반 카테고리들 먼저 추가
      Object.values(categorizedGroups).forEach((group) => {
        group.forEach((row, index) => {
          sortedRows.push({
            ...row,
            categoryName: index === 0 ? row.categoryName : '',
            isFirstInCategory: index === 0,
            categoryRowspan: group.length,
          });
        });
      });
      
      // 미분류를 마지막에 추가
      if (uncategorizedGroup.length > 0) {
        uncategorizedGroup.forEach((row, index) => {
          sortedRows.push({
            ...row,
            categoryName: index === 0 ? '미분류' : '',
            isFirstInCategory: index === 0,
            categoryRowspan: uncategorizedGroup.length,
          });
        });
      }
      
      setCategorySoftwareTop3(sortedRows);
    } catch (error) {
      console.error('Failed to load category software top3:', error);
    } finally {
      setIsLoadingCategoryTop3(false);
    }
  };

  return (
    <div className="tab-panel">
      <h2>🚫 소프트웨어차단</h2>
      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      
      {/* 소프트웨어 차단 현황 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>소프트웨어 차단 현황</span>
          {(() => {
            const sblockLink = attachments.find(link => link.linkId === 'dlp_attach_sblock');
            return sblockLink ? (
              <a 
                href={sblockLink.linkUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  marginLeft: '0.5rem',
                  color: '#3498db',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                [{sblockLink.linkName}]
              </a>
            ) : null;
          })()}
        </div>
        {/* 1x2 표: 소프트웨어 차단 건수 */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>소프트웨어 차단 건수</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {isLoadingBlockCount ? (
                    '로딩 중...'
                  ) : (
                    softwareBlockCount.toLocaleString()
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 소프트웨어 차단 사용자 TOP 5 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>소프트웨어 차단 사용자 TOP 5</span>
        </div>
        {/* 5x13 표: 부서, 이름, 차단 소프트웨어, 프로세스명, 차단수 (행수는 데이터에 의해 가변적) */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>부서</th>
                <th>이름</th>
                <th>차단 소프트웨어</th>
                <th>프로세스명</th>
                <th>차단수</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingUserTop5 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : softwareBlockUserTop5.length > 0 ? (
                softwareBlockUserTop5.map((row, index) => (
                  <tr key={index}>
                    {row.isFirstInUser && (
                      <td rowSpan={row.userRowspan}>{row.department || '-'}</td>
                    )}
                    {row.isFirstInUser && (
                      <td rowSpan={row.userRowspan}>{row.name || '-'}</td>
                    )}
                    <td>{row.blockedSoftware || '-'}</td>
                    <td>{row.processName || '-'}</td>
                    <td>{row.blockCount ? row.blockCount.toLocaleString() : '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 카테고리 별 차단 소프트웨어 TOP 3 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>카테고리 별 차단 소프트웨어 TOP 3</span>
        </div>
        {/* 5x16 표: 행수는 데이터에 의해 가변적 */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>카테고리</th>
                <th>차단 소프트웨어</th>
                <th>프로세스명</th>
                <th>차단수</th>
                <th>비고</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingCategoryTop3 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : categorySoftwareTop3.length > 0 ? (
                categorySoftwareTop3.map((row, index) => (
                  <tr key={index}>
                    {row.isFirstInCategory && (
                      <td rowSpan={row.categoryRowspan}>{row.categoryName || '-'}</td>
                    )}
                    <td>{row.blockedSoftware || '-'}</td>
                    <td>{row.processName || '-'}</td>
                    <td>{row.blockCount ? row.blockCount.toLocaleString() : '-'}</td>
                    <td>-</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666', fontStyle: 'italic', textAlign: 'right' }}>
          ※ 일부 소프트웨어의 경우 라이브러리 사용 등으로 인해 해시값이 달라져 차단되는 경우가 있어, 모니터링을 통해 반영하고 있습니다.
        </div>
      </div>
    </div>
  );
};

// 웹사이트차단 탭
const WebsiteBlockTab: React.FC<{ period: string; company: string; attachments: any[] }> = ({ period, company, attachments }) => {
  const [websiteBlockCount, setWebsiteBlockCount] = useState<number>(0);
  const [isLoadingBlockCount, setIsLoadingBlockCount] = useState(false);
  const [websiteBlockUserTop5, setWebsiteBlockUserTop5] = useState<any[]>([]);
  const [isLoadingUserTop5, setIsLoadingUserTop5] = useState(false);
  const [categoryWebsiteTop3, setCategoryWebsiteTop3] = useState<any[]>([]);
  const [isLoadingCategoryTop3, setIsLoadingCategoryTop3] = useState(false);

  useEffect(() => {
    if (period && company) {
      loadWebsiteBlockCount();
      loadWebsiteBlockUserTop5();
      loadCategoryWebsiteTop3();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, company]);

  const loadWebsiteBlockCount = async () => {
    if (!period || !company) return;
    setIsLoadingBlockCount(true);
    try {
      const count = await dlpService.getWebsiteBlockCount(period, company);
      setWebsiteBlockCount(count);
    } catch (error) {
      console.error('Failed to load website block count:', error);
    } finally {
      setIsLoadingBlockCount(false);
    }
  };

  const loadWebsiteBlockUserTop5 = async () => {
    if (!period || !company) return;
    setIsLoadingUserTop5(true);
    try {
      const data = await dlpService.getWebsiteBlockUserTop5(period, company);
      setWebsiteBlockUserTop5(data);
    } catch (error) {
      console.error('Failed to load website block user top5:', error);
    } finally {
      setIsLoadingUserTop5(false);
    }
  };

  const loadCategoryWebsiteTop3 = async () => {
    if (!period || !company) return;
    setIsLoadingCategoryTop3(true);
    try {
      const data = await dlpService.getCategoryWebsiteTop3(period, company);
      setCategoryWebsiteTop3(data);
    } catch (error) {
      console.error('Failed to load category website top3:', error);
    } finally {
      setIsLoadingCategoryTop3(false);
    }
  };

  return (
    <div className="tab-panel">
      <h2>🌐 웹사이트차단</h2>
      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      
      {/* 웹사이트 차단 현황 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>웹사이트 차단 현황</span>
          {(() => {
            const wblockLink = attachments.find(link => link.linkId === 'dlp_attach_wblock');
            return wblockLink ? (
              <a 
                href={wblockLink.linkUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  marginLeft: '0.5rem',
                  color: '#3498db',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                [{wblockLink.linkName}]
              </a>
            ) : null;
          })()}
        </div>
        {/* 1x2 표: 웹사이트 차단 건수 */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>웹사이트 차단 건수</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {isLoadingBlockCount ? (
                    '로딩 중...'
                  ) : (
                    websiteBlockCount.toLocaleString()
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 웹사이트 차단 사용자 TOP 5 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>웹사이트 차단 사용자 TOP 5</span>
        </div>
        {/* 6x26 표: 부서, 이름, 차단 URL, 차단 웹사이트, 카테고리, 차단수 (행수는 데이터에 의해 가변적) */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>부서</th>
                <th>이름</th>
                <th>차단 URL</th>
                <th>차단 웹사이트</th>
                <th>차단수</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingUserTop5 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : websiteBlockUserTop5.length > 0 ? (
                websiteBlockUserTop5.map((row, index) => (
                  <tr key={index}>
                    {row.isFirstInUser && (
                      <td rowSpan={row.userRowspan}>{row.department || '-'}</td>
                    )}
                    {row.isFirstInUser && (
                      <td rowSpan={row.userRowspan}>{row.name || '-'}</td>
                    )}
                    <td>{row.blockedUrl || '-'}</td>
                    <td>{row.blockedWebsite || '-'}</td>
                    <td>{row.blockCount ? row.blockCount.toLocaleString() : '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 카테고리 별 차단 웹사이트 TOP 3 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>카테고리 별 차단 웹사이트 TOP 3</span>
        </div>
        {/* 4x25 표: 카테고리, 차단 URL, 차단 웹사이트, 차단수 (행수는 데이터에 의해 가변적) */}
        <div className="policy-table-container">
          <table className="policy-table">
            <thead>
              <tr>
                <th>카테고리</th>
                <th>차단 URL</th>
                <th>차단 웹사이트</th>
                <th>차단수</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingCategoryTop3 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '1rem' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : categoryWebsiteTop3.length > 0 ? (
                categoryWebsiteTop3.map((row, index) => (
                  <tr key={index}>
                    {row.isFirstInCategory && (
                      <td rowSpan={row.categoryRowspan}>{row.categoryName || '-'}</td>
                    )}
                    <td>{row.blockedUrl || '-'}</td>
                    <td>{row.blockedWebsite || '-'}</td>
                    <td>{row.blockCount ? row.blockCount.toLocaleString() : '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '1rem' }}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// 웹사이트 접속 현황 내 이슈 사이트 탭
const WebsiteAccessTab: React.FC<{ period: string; company: string; attachments: any[] }> = ({ period, company }) => {
  const [issueSites, setIssueSites] = useState<any[]>([]);
  const [commonBlockedSites, setCommonBlockedSites] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (period && company) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, company]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [issueSitesData, commonBlockedSitesData] = await Promise.all([
        dlpService.getIssueSites(period, company),
        dlpService.getCommonBlockedSites(period, company),
      ]);
      setIssueSites(issueSitesData);
      setCommonBlockedSites(commonBlockedSitesData);
    } catch (error) {
      console.error('Failed to load website access data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tab-panel">
      <h2>⚠️ 당월 유해사이트 등록 현황</h2>
      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      
      {/* 웹사이트 접속 현황 내 이슈 사이트 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>웹사이트 접속 현황 내 이슈 사이트</span>
        </div>
        {isLoading ? (
          <div className="loading">로딩 중...</div>
        ) : (
          <div className="policy-table-container">
            <table className="policy-table">
              <thead>
                <tr>
                  <th>카테고리</th>
                  <th>접속 URL</th>
                  <th>이슈사항</th>
                </tr>
              </thead>
              <tbody>
                {issueSites.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center' }}>데이터가 없습니다.</td>
                  </tr>
                ) : (
                  issueSites.map((site, index) => (
                    <tr key={site.id || index}>
                      <td>{site.category || '-'}</td>
                      <td>{site.url || '-'}</td>
                      <td>{site.issueMemo || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 전사 공통 유해사이트 차단 내역 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>전사 공통 유해사이트 차단 내역</span>
        </div>
        {isLoading ? (
          <div className="loading">로딩 중...</div>
        ) : (
          <div className="policy-table-container">
            <table className="policy-table">
              <thead>
                <tr>
                  <th>카테고리</th>
                  <th>접속URL</th>
                  <th>이슈사항</th>
                </tr>
              </thead>
              <tbody>
                {commonBlockedSites.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center' }}>데이터가 없습니다.</td>
                  </tr>
                ) : (
                  commonBlockedSites.map((site, index) => (
                    <tr key={site.id || index}>
                      <td>{site.category || '-'}</td>
                      <td>{site.url || '-'}</td>
                      <td>{site.issueMemo || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// DLP 라이선스현황 탭
const LicenseTab: React.FC<{ period: string; company: string }> = ({ period, company }) => {
  const [licenseStat, setLicenseStat] = useState<any>(null);
  const [exEmployees, setExEmployees] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (period && company) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, company]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [licenseStatData, exEmployeesData] = await Promise.all([
        dlpService.getLicenseStat(period, company),
        dlpService.getExEmployees(period, company),
      ]);
      setLicenseStat(licenseStatData);
      setExEmployees(exEmployeesData);
    } catch (error) {
      console.error('Failed to load license data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tab-panel">
      <h2>🔑 DLP 라이선스현황</h2>
      <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      
      {/* DLP 라이선스 현황 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>DLP 라이선스 현황</span>
        </div>
        {isLoading ? (
          <div className="loading">로딩 중...</div>
        ) : (
          <div className="policy-table-container">
            <table className="policy-table">
              <thead>
                <tr>
                  <th>전체 총수량</th>
                  <th>구매수량</th>
                  <th>사용수량</th>
                  <th>1개월 이상 미사용수량</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{licenseStat?.totalAmount != null ? licenseStat.totalAmount.toLocaleString() : '-'}</td>
                  <td>{licenseStat?.companyAmount != null ? licenseStat.companyAmount.toLocaleString() : '-'}</td>
                  <td>{licenseStat?.companyUsedAmount != null ? licenseStat.companyUsedAmount.toLocaleString() : '-'}</td>
                  <td style={{ color: 'red' }}>
                    {licenseStat?.unusedAmount != null ? licenseStat.unusedAmount.toLocaleString() : '-'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 퇴사자 라이선스 현황 */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <span style={{ marginRight: '0.5rem', fontSize: '1rem' }}>📋</span>
          <span style={{ fontSize: '1rem', fontWeight: 500 }}>퇴사자 라이선스 현황</span>
        </div>
        {isLoading ? (
          <div className="loading">로딩 중...</div>
        ) : (
          <div className="policy-table-container">
            <table className="policy-table">
              <thead>
                <tr>
                  <th>부서</th>
                  <th>이름</th>
                  <th>PC IP</th>
                  <th>버전정보</th>
                  <th>최종인증시각</th>
                  <th>NAC 최종 인증자</th>
                </tr>
              </thead>
              <tbody>
                {exEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center' }}>데이터가 없습니다.</td>
                  </tr>
                ) : (
                  exEmployees.map((employee, index) => (
                    <tr key={employee.id || index}>
                      <td>퇴사</td>
                      <td>{employee.name || '-'}</td>
                      <td>{employee.pcip || '-'}</td>
                      <td>{employee.verInfo || '-'}</td>
                      <td>
                        {employee.lastLoginTime
                          ? new Date(employee.lastLoginTime).toLocaleString('ko-KR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                            })
                          : '-'}
                      </td>
                      <td>{employee.nacAthenticator || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DlpReportPage;

